import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Building2, Save, Loader2, Upload, Trash2, ImageIcon, Bell, BellOff, Send, Mail, User, KeyRound, AtSign } from "lucide-react";
import { toast } from "sonner";
import { PhoneAlertsCard } from "@/components/settings/PhoneAlertsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage({ user, onLogout, onUserUpdate }) {
  const { t } = useTranslation();
  const [companyName, setCompanyName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const isOwner = user?.role === "owner";

  // Notification preferences
  const [eventTypes, setEventTypes] = useState({});
  const [notifPrefs, setNotifPrefs] = useState({ mute_all: false, events: {} });
  const [savingNotifs, setSavingNotifs] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Account settings
  const [loginEmail, setLoginEmail] = useState(user?.email || "");
  const [emailPassword, setEmailPassword] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [savingNotifEmail, setSavingNotifEmail] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    loadSettings();
    loadNotificationPrefs();
    loadAccountSettings();
  }, []);

  const loadAccountSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setLoginEmail(data.email || "");
      setNotificationEmail(data.notification_email || "");
    } catch {}
  };

  const saveLoginEmail = async (e) => {
    e.preventDefault();
    if (!emailPassword) { toast.error("Please enter your password to confirm"); return; }
    setSavingEmail(true);
    try {
      const { data } = await axios.put(`${API}/auth/email`, {
        new_email: loginEmail,
        password: emailPassword,
      });
      toast.success(data.message);
      // Update token and user info
      if (data.access_token) {
        localStorage.setItem("token", data.access_token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
        if (onUserUpdate) onUserUpdate(data.user);
      }
      setEmailPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update email");
    } finally { setSavingEmail(false); }
  };

  const saveNotificationEmail = async (e) => {
    e.preventDefault();
    setSavingNotifEmail(true);
    try {
      const { data } = await axios.put(`${API}/auth/notification-email`, {
        notification_email: notificationEmail,
      });
      toast.success(data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update notification email");
    } finally { setSavingNotifEmail(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingPassword(true);
    try {
      const { data } = await axios.put(`${API}/auth/password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(data.message);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally { setSavingPassword(false); }
  };

  const loadSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      setCompanyName(data.company_name || "");
      setReplyTo(data.reply_to_email || "");
      setLogoUrl(data.logo_url || null);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationPrefs = async () => {
    try {
      const [typesRes, prefsRes] = await Promise.all([
        axios.get(`${API}/notifications/event-types`),
        axios.get(`${API}/notifications/preferences`)
      ]);
      setEventTypes(typesRes.data);
      setNotifPrefs(prefsRes.data);
    } catch (error) {
      console.error("Failed to load notification preferences");
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    if (!isOwner) return;
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, { company_name: companyName, reply_to_email: replyTo });
      toast.success("Settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, WebP, or SVG file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo must be less than 5MB");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/settings/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setLogoUrl(data.logo_url);
      toast.success("Logo uploaded");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm("Remove company logo?")) return;
    try {
      await axios.delete(`${API}/settings/logo`);
      setLogoUrl(null);
      toast.success("Logo removed");
    } catch (error) {
      toast.error("Failed to remove logo");
    }
  };

  const toggleMuteAll = async () => {
    const newMuteAll = !notifPrefs.mute_all;
    setNotifPrefs(prev => ({ ...prev, mute_all: newMuteAll }));
    try {
      await axios.put(`${API}/notifications/preferences`, { mute_all: newMuteAll });
      toast.success(newMuteAll ? "All notifications muted" : "Notifications enabled");
    } catch {
      setNotifPrefs(prev => ({ ...prev, mute_all: !newMuteAll }));
      toast.error("Failed to update preferences");
    }
  };

  const toggleEventType = async (eventKey) => {
    const current = notifPrefs.events[eventKey] ?? true;
    const newEvents = { ...notifPrefs.events, [eventKey]: !current };
    setNotifPrefs(prev => ({ ...prev, events: newEvents }));
    try {
      await axios.put(`${API}/notifications/preferences`, { events: newEvents });
    } catch {
      newEvents[eventKey] = current;
      setNotifPrefs(prev => ({ ...prev, events: newEvents }));
      toast.error("Failed to update preferences");
    }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const { data } = await axios.post(`${API}/notifications/test`);
      toast.success(data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-slate-500">{t("settings.loadingSettings")}</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div data-testid="settings-page" className="space-y-6 max-w-2xl">
        {/* Header */}
        <div>
          <p className="text-xs font-semibold text-indigo-600 uppercase tracking-widest mb-1">{t("settings.configuration")}</p>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight uppercase">{t("settings.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("settings.subtitle")}</p>
        </div>

        {/* Company Settings Card */}
        <div className="border border-slate-200 rounded-sm bg-white">
          <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-3">
            <Building2 className="w-5 h-5 text-indigo-600" />
            <h2 className="font-display text-lg font-semibold text-slate-900 uppercase tracking-wide">{t("settings.companyInfo")}</h2>
          </div>
          <div className="p-6 space-y-6">
            {/* Logo Section */}
            <div className="space-y-3">
              <Label className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("settings.companyLogo")}</Label>
              <div className="flex items-start gap-5">
                <div data-testid="logo-preview" className="w-28 h-28 rounded-sm border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {logoUrl ? (
                    <img src={`${BASE_URL}${logoUrl}`} alt="Company logo" className="w-full h-full object-contain p-1" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-slate-300" />
                  )}
                </div>
                <div className="space-y-2 pt-1 min-w-0">
                  <p className="text-xs text-slate-400">{t("settings.logoHint")}</p>
                  {isOwner ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
                      <Button type="button" variant="outline" size="sm" data-testid="upload-logo-btn" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="rounded-sm text-xs">
                        {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                        {logoUrl ? t("settings.replace") : t("common.upload")}
                      </Button>
                      {logoUrl && (
                        <Button type="button" variant="outline" size="sm" data-testid="delete-logo-btn" onClick={handleLogoDelete} className="rounded-sm text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />{t("settings.remove")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-amber-600 font-medium">{t("settings.ownerOnlyLogo")}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100" />
            {/* Company Name */}
            <form onSubmit={saveSettings} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="company-name" className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("settings.companyName")}</Label>
                <Input id="company-name" data-testid="company-name-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder={t("settings.companyNamePlaceholder")} className="rounded-sm border-slate-200 max-w-md" disabled={!isOwner} />
                <p className="text-xs text-slate-400">{t("settings.companyNameHint")}</p>
                {!isOwner && <p data-testid="owner-only-notice" className="text-xs text-amber-600 font-medium">{t("settings.ownerOnlySettings")}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="reply-to-email" className="text-xs font-medium text-slate-600 uppercase tracking-wider">{t("settings.replyToEmail")}</Label>
                <Input id="reply-to-email" type="email" data-testid="reply-to-email-input" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="office@yourcompany.com" className="rounded-sm border-slate-200 max-w-md" disabled={!isOwner} />
                <p className="text-xs text-slate-400">{t("settings.replyToEmailHint")}</p>
              </div>
              {isOwner && (
                <Button type="submit" data-testid="save-settings-btn" disabled={saving} className="rounded-sm bg-indigo-600 hover:bg-indigo-700">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {t("settings.saveChanges")}
                </Button>
              )}
            </form>
          </div>
        </div>

        {/* Notification Preferences Card */}
        <div className="border border-slate-200 rounded-sm bg-white">
          <div className="border-b border-slate-100 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-indigo-600" />
              <h2 className="font-display text-lg font-semibold text-slate-900 uppercase tracking-wide">{t("settings.emailNotifications")}</h2>
            </div>
            <Button variant="outline" size="sm" data-testid="send-test-email-btn" disabled={sendingTest} onClick={sendTestEmail} className="rounded-sm text-xs">
              {sendingTest ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              {t("settings.sendTestEmail")}
            </Button>
          </div>
          <div className="p-6 space-y-5">
            {/* Mute All Toggle */}
            <div className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-sm border border-slate-100">
              <div className="flex items-center gap-3">
                <BellOff className="w-4 h-4 text-slate-500" />
                <div>
                  <p className="text-sm font-medium text-slate-800">{t("settings.muteAll")}</p>
                  <p className="text-xs text-slate-500">{t("settings.muteAllDesc")}</p>
                </div>
              </div>
              <Switch data-testid="mute-all-toggle" checked={notifPrefs.mute_all} onCheckedChange={toggleMuteAll} />
            </div>

            {/* Event Type Toggles */}
            <div className={`space-y-1 ${notifPrefs.mute_all ? "opacity-50 pointer-events-none" : ""}`}>
              <p className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-3">{t("settings.notificationTypes")}</p>
              {Object.entries(eventTypes).map(([key, config]) => (
                <div key={key} className="flex items-center justify-between py-3 px-4 hover:bg-slate-50/50 rounded-sm transition-colors" data-testid={`notif-toggle-${key}`}>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">{config.label}</p>
                      <p className="text-xs text-slate-400">{config.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={notifPrefs.events[key] ?? config.default_enabled}
                    onCheckedChange={() => toggleEventType(key)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Account Settings Card */}
        <div className="border border-slate-200 rounded-sm bg-white">
          <div className="border-b border-slate-100 px-6 py-4 flex items-center gap-3">
            <User className="w-5 h-5 text-indigo-600" />
            <div>
              <h2 className="text-base font-semibold text-slate-900 uppercase tracking-tight">{t("settings.accountSettings")}</h2>
              <p className="text-xs text-slate-500">{t("settings.accountSubtitle")}</p>
            </div>
          </div>
          <div className="p-6 space-y-8">
            {/* Change Login Email */}
            <form onSubmit={saveLoginEmail} className="space-y-3" data-testid="change-email-form">
              <div className="flex items-center gap-2 mb-1">
                <AtSign className="w-4 h-4 text-slate-500" />
                <p className="text-sm font-semibold text-slate-700">{t("settings.loginEmail")}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">{t("settings.newEmail")}</Label>
                  <Input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="your@email.com"
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">{t("settings.confirmWithPassword")}</Label>
                  <Input
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    placeholder="Enter current password"
                    data-testid="email-confirm-password"
                  />
                </div>
              </div>
              <Button type="submit" size="sm" disabled={savingEmail || !loginEmail || !emailPassword} data-testid="save-email-btn">
                {savingEmail ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />{t("settings.saving")}</> : <><Save className="w-3 h-3 mr-1.5" />{t("settings.updateEmail")}</>}
              </Button>
            </form>

            <div className="border-t border-slate-100" />

            {/* Notification Email */}
            <form onSubmit={saveNotificationEmail} className="space-y-3" data-testid="notification-email-form">
              <div className="flex items-center gap-2 mb-1">
                <Bell className="w-4 h-4 text-slate-500" />
                <p className="text-sm font-semibold text-slate-700">{t("settings.notificationEmail")}</p>
              </div>
              <p className="text-xs text-slate-500">{t("settings.notificationEmailHint")}</p>
              <div className="flex gap-3 items-end">
                <div className="flex-1 space-y-1">
                  <Input
                    type="email"
                    value={notificationEmail}
                    onChange={(e) => setNotificationEmail(e.target.value)}
                    placeholder={loginEmail || "notifications@example.com"}
                    data-testid="notification-email-input"
                  />
                </div>
                <Button type="submit" size="sm" disabled={savingNotifEmail} data-testid="save-notif-email-btn">
                  {savingNotifEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1.5" />{t("common.save")}</>}
                </Button>
              </div>
            </form>

            <div className="border-t border-slate-100" />

            {/* Change Password */}
            <form onSubmit={savePassword} className="space-y-3" data-testid="change-password-form">
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-4 h-4 text-slate-500" />
                <p className="text-sm font-semibold text-slate-700">{t("settings.changePassword")}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">{t("settings.currentPassword")}</Label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password"
                    data-testid="current-password-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">{t("settings.newPassword")}</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t("settings.newPassword")}
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">{t("settings.confirmNewPassword")}</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button type="submit" size="sm" disabled={savingPassword || !currentPassword || !newPassword || !confirmPassword} data-testid="save-password-btn">
                {savingPassword ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />{t("settings.saving")}</> : <><KeyRound className="w-3 h-3 mr-1.5" />{t("settings.updatePassword")}</>}
              </Button>
            </form>
          </div>
        </div>
        <PhoneAlertsCard isOwner={user?.role === "owner"} />
      </div>
    </Layout>
  );
}
